import React, { useCallback, useEffect, useRef, useState } from 'react';
import BootstrapAlert from 'react-bootstrap/Alert';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useMount } from 'react-use';
import classnames from 'classnames';

import Icon from '../icon/Icon';
import { alertConstants } from '../../redux/constants/alert';
import { useAppDispatch } from '../../hooks/useAppDispatch';

const getAlertIcon = (variant) => {
  switch (variant) {
    case 'info':
      return 'information';
    default:
      return '';
  }
};

const Alert = () => {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const [open, setOpen] = useState(false);
  const [alertDetails, setAlertDetails] = useState({
    message: '',
    variant: '',
    className: '',
    dismissible: true,
    persistOnNavigation: false,
  });
  const alertReducer = useSelector((state) => state.alerts.alert);
  const autoDismissTimeoutRef = useRef(null);
  const onDismissRef = useRef();
  const persistOnNavigationRef = useRef(false);

  const handleClose = useCallback(
    (reason = 'external') => {
      if (autoDismissTimeoutRef.current) {
        clearTimeout(autoDismissTimeoutRef.current);
        autoDismissTimeoutRef.current = null;
      }
      setOpen(false);
      if (reason === 'manual') {
        onDismissRef.current?.();
      }
      dispatch({ type: alertConstants.ALERT_RESET });
    },
    [dispatch]
  );

  useMount(() =>
    history.listen((location, action) => {
      if (action === 'PUSH' && !persistOnNavigationRef.current) {
        handleClose('navigation');
      }
    })
  );

  useEffect(() => {
    onDismissRef.current = alertReducer.onDismiss ?? null;
    persistOnNavigationRef.current = Boolean(alertReducer.persistOnNavigation);

    if (autoDismissTimeoutRef.current) {
      clearTimeout(autoDismissTimeoutRef.current);
      autoDismissTimeoutRef.current = null;
    }

    if (!alertReducer.message || !alertReducer.variant) {
      setOpen(false);
      return undefined;
    }

    setAlertDetails(alertReducer);
    setOpen(true);

    if (alertReducer.dismissible && alertReducer.autoDismiss !== false) {
      autoDismissTimeoutRef.current = setTimeout(
        () => handleClose('auto'),
        6000
      );
    }

    return () => {
      if (autoDismissTimeoutRef.current) {
        clearTimeout(autoDismissTimeoutRef.current);
        autoDismissTimeoutRef.current = null;
      }
    };
  }, [alertReducer, handleClose]);

  return (
    <BootstrapAlert
      className={classnames({
        hasIcon: alertDetails.icon ?? getAlertIcon(alertDetails.variant),
        [alertDetails.className]: alertDetails.className,
      })}
      show={open}
      variant={alertDetails.variant}
      onClose={() => handleClose('manual')}
      dismissible={alertDetails.dismissible}
    >
      {(getAlertIcon(alertDetails.variant) || alertDetails.icon) && (
        <Icon icon={alertDetails.icon ?? getAlertIcon(alertDetails.variant)} />
      )}
      {alertDetails.message}
    </BootstrapAlert>
  );
};

export default Alert;
